import { useTranslation } from 'react-i18next'
import { AuthorizationProvider } from 'src/components/common/Authorization'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { AuthorizationType } from 'src/enums/auth'
import { permissionValue } from 'src/enums/permissionValue'
import { getThemeColor } from 'src/utils'
import { ReactComponent as AddIcon } from '../../assets/svg/addProjectIcon.svg'
import { useDialogCtx, useUserCtx } from 'src/hooks/context-hooks'
import SetupFacilityDialog from './SetupFacilityDialog'
import { IProjectInfo } from 'src/generated-types/project'
import ProjectDialog from './ProjectDialog'
import LocalityDialog from './locality/LocalityDialog'
import CustomerDialog from './customer/CustomerDialog'
import { Fab, useMediaQuery, useTheme } from '@mui/material'
import { MUIThemeColors } from 'src/mui-theme'
import { Add } from '@mui/icons-material'
import { IMooringType, IPositionWithDrawing } from 'src/generated-types'

const CreateProjectButton = () => {
  const { t: translate } = useTranslation('projects')
  const { user } = useUserCtx()
  const { showDialog } = useDialogCtx()
  const theme = useTheme()
  const isMobile = useMediaQuery<any>(theme.breakpoints.down('sm'))

  const getStepStates = (stepLabels: string[], activeStepIdx: number) =>
    stepLabels.map((label, idx) => ({
      label,
      completed: idx < activeStepIdx,
      active: idx === activeStepIdx,
    }))

  const stepLabels = [
    translate('select-customer', { ns: 'admin-customer' }),
    translate('select-locality', { ns: 'locality' }),
    translate('add-project', { ns: 'projects' }),
    translate('setup-facility', { ns: 'projects' }),
  ]

  const onSetupFacility = (project: IProjectInfo) => {
    showDialog(
      SetupFacilityDialog,
      {
        componentProps: {
          project: project,
          customerId: project.customerId as EntityId,
          localityId: project.localityId as EntityId,
          withSteps: true,
          steps: getStepStates(stepLabels, 3),
          isConfigurePositions: false,
          onStepBack: (
            positions?: IPositionWithDrawing[],
            type?: IMooringType,
            noOfCages?: number,
            noOfPens?: number,
            noOfRows?: number,
            gridLineWidthInMeters?: number | null,
            gridLineHeightInMeters?: number | null,
          ) => {
            project.positions = positions
            project.type = type
            project.noOfCages = noOfCages
            project.noOfPens = noOfPens
            project.noOfRows = noOfRows
            project.gridLineWidthInMeters = gridLineWidthInMeters ?? undefined
            project.gridLineHeightInMeters = gridLineHeightInMeters ?? undefined
            handleProjectDialog(project.customerId as number, project.localityId as number, project, true)
          },
        },
        title: translate('setup-facility'),
        opts: {
          maxWidth: 'md',
          fullWidth: true,
        },
      },
      undefined,
      false,
    )
  }

  const handleProjectDialog = (
    customerId: number,
    localityId: number,
    project?: IProjectInfo,
    isProjectUpdate = false,
  ) => {
    showDialog(
      ProjectDialog,
      {
        opts: {
          maxWidth: 'md',
          fullWidth: true,
        },
        componentProps: {
          customerId: customerId,
          localityId: localityId,
          withSteps: true,
          steps: getStepStates(stepLabels, 2),
          onStepSuccess: (project: IProjectInfo) => onSetupFacility(project),
          onStepBack: () => handleLocalityDialog(customerId, localityId),
          initialValues: project,
          isProjectUpdate: isProjectUpdate,
        },
        title: translate('add-new-project', { ns: 'projects' }),
      },
      undefined,
      false,
    )
  }

  const handleLocalityDialog = (customerId: number, localityId?: number) => {
    showDialog(
      LocalityDialog,
      {
        opts: {
          maxWidth: 'md',
          fullWidth: true,
        },
        title: translate('add-new-project', { ns: 'projects' }),
        componentProps: {
          customerId: customerId,
          withSteps: true,
          steps: getStepStates(stepLabels, 1),
          onStepSuccess: (customerId: number, localityId: number, localityName?: string, locationNr?: string) =>
            handleProjectDialog(customerId, localityId, {
              name: `${localityName} #${locationNr || ''} | `,
            } as IProjectInfo),
          onCancel: e => handleProjectCreation(e, customerId),
          localityId: localityId,
        },
      },
      undefined,
      false,
    )
  }

  const handleProjectCreation = (e, customerId?: number) => {
    e.stopPropagation()
    showDialog(
      CustomerDialog,
      {
        opts: {
          maxWidth: 'md',
          fullWidth: true,
        },
        componentProps: {
          withSteps: true,
          steps: getStepStates(stepLabels, 0),
          onStepSuccess: (customerId: number) => handleLocalityDialog(customerId),
          customerId: customerId,
        },
        title: translate('add-new-project', { ns: 'projects' }),
      },
      undefined,
      false,
    )
  }

  // Mobile FAB button
  if (isMobile) {
    return (
      <AuthorizationProvider
        self_auth={true}
        permission={user?.permission}
        permissions={[permissionValue.SystemProjectAdminPermission, permissionValue.SystemAdminPermission]}
        type={AuthorizationType.Disabled}
        key={'add-project-mobile'}
      >
        <Fab
          onClick={e => {
            handleProjectCreation(e)
          }}
          sx={{
            position: 'fixed',
            bottom: 110,
            right: 24,
            zIndex: 9999,
            width: 56,
            height: 56,
            backgroundColor: getThemeColor(theme, MUIThemeColors.primaryMain),
            '&:hover': {
              backgroundColor: getThemeColor(theme, MUIThemeColors.primaryDark),
            },
          }}
          aria-label={translate('add-new-project', { ns: 'projects' })}
        >
          <Add sx={{ color: getThemeColor(theme, MUIThemeColors.white), fontSize: 24 }} />
        </Fab>
      </AuthorizationProvider>
    )
  }

  // Desktop button
  return (
    <AuthorizationProvider
      self_auth={true}
      permission={user?.permission}
      permissions={[permissionValue.SystemProjectAdminPermission, permissionValue.SystemAdminPermission]}
      type={AuthorizationType.Hidden}
      key={'add-project'}
    >
      <GridActionButton
        variant="outlined"
        onClick={e => {
          handleProjectCreation(e)
        }}
        sx={{ minWidth: '10px', padding: '9px 9px' }}
        buttonText={translate('add-new-project', { ns: 'projects' })}
        buttonIcon={<AddIcon />}
        textColor={getThemeColor(theme, MUIThemeColors.white)}
        shadowWeight={null}
        bgColor={getThemeColor(theme, MUIThemeColors.primaryMain)}
        buttonTextProps={{ fontWeight: 500, textTransform: 'uppercase' }}
        height="44px"
      />
    </AuthorizationProvider>
  )
}

export default CreateProjectButton
